import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
    BeforeInstallPromptEvent,
    clearQrEntryParam,
    isIosDevice,
    isQrCodeEntry,
    isStandalonePwa,
} from '../../utils/pwa';
import { AddToHomeScreenInfoDialog } from './AddToHomeScreenInfo';

/**
 * When the user opens the backoffice from the QR code on a phone, offer PWA install:
 * - Android/Chrome: native install prompt (one tap, browser security still applies)
 * - iOS/Safari: manual "Add to Home Screen" guide (no programmatic API on iOS)
 */
const PwaQrInstallPrompt: React.FC = () => {
    const translate = useTranslate();
    const isMobile = useIsMobile();
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showAndroidInstall, setShowAndroidInstall] = useState(false);
    const [showManualGuide, setShowManualGuide] = useState(false);
    const [installing, setInstalling] = useState(false);

    const shouldOfferInstall = isMobile && isQrCodeEntry() && !isStandalonePwa();

    useEffect(() => {
        const handler = (event: Event) => {
            event.preventDefault();
            setDeferredPrompt(event as BeforeInstallPromptEvent);
        };

        window.addEventListener('beforeinstallprompt', handler);

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    useEffect(() => {
        if (!shouldOfferInstall) {
            return;
        }

        if (isIosDevice()) {
            setShowManualGuide(true);
            return;
        }

        if (deferredPrompt) {
            setShowAndroidInstall(true);
            return;
        }

        const fallbackTimer = window.setTimeout(() => {
            setShowManualGuide(true);
        }, 1500);

        return () => window.clearTimeout(fallbackTimer);
    }, [shouldOfferInstall, deferredPrompt]);

    const closePrompts = () => {
        clearQrEntryParam();
        setShowAndroidInstall(false);
        setShowManualGuide(false);
    };

    const handleInstall = async () => {
        if (!deferredPrompt) {
            return;
        }

        setInstalling(true);

        try {
            await deferredPrompt.prompt();
            await deferredPrompt.userChoice;
        } finally {
            setInstalling(false);
            setDeferredPrompt(null);
            closePrompts();
        }
    };

    if (!shouldOfferInstall) {
        return null;
    }

    return (
        <>
            <Dialog open={showAndroidInstall} onClose={closePrompts} maxWidth='xs' fullWidth>
                <DialogTitle>{translate('app.pwa.install.title')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant='body2' color='textSecondary'>
                        {translate('app.pwa.install.message')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePrompts} color='default'>
                        {translate('app.pwa.install.later')}
                    </Button>
                    <Button
                        onClick={handleInstall}
                        color='primary'
                        variant='contained'
                        startIcon={installing ? <CircularProgress size={18} color='inherit' /> : <GetAppIcon />}
                        disabled={installing || !deferredPrompt}
                    >
                        {translate('app.pwa.install.action')}
                    </Button>
                </DialogActions>
            </Dialog>

            <AddToHomeScreenInfoDialog open={showManualGuide} onClose={closePrompts} />
        </>
    );
};

export default PwaQrInstallPrompt;
